import { useState, useRef, useCallback, useEffect } from 'react';

export const BeforeAfterSlider = ({ 
  beforeImage, 
  afterImage, 
  beforeLabel = 'Before', 
  afterLabel = 'After',
  className = ''
}) => {
  const [afterLoaded, setAfterLoaded] = useState(false);
  const [beforeLoaded, setBeforeLoaded] = useState(false);
  const containerRef = useRef(null);
  const beforeRef = useRef(null);
  const afterRef = useRef(null);
  const sliderLineRef = useRef(null);
  const beforeClipRef = useRef(null);
  const beforeLabelRef = useRef(null);
  const afterLabelRef = useRef(null);
  const isDragging = useRef(false);
  const rafId = useRef(null);

  useEffect(() => {
    setAfterLoaded(false);
    setBeforeLoaded(false);
  }, [beforeImage, afterImage]);

  useEffect(() => {
    const checkCached = () => {
      if (beforeRef.current?.complete && beforeRef.current.naturalWidth > 50) setBeforeLoaded(true);
      if (afterRef.current?.complete && afterRef.current.naturalWidth > 50) setAfterLoaded(true);
    };
    checkCached();
    const timer = setTimeout(checkCached, 100);
    return () => clearTimeout(timer);
  }, [beforeImage, afterImage]);

  // Direct DOM updates — no React state, no re-renders
  const updateSlider = useCallback((pct) => {
    if (sliderLineRef.current) sliderLineRef.current.style.left = `${pct}%`;
    if (beforeClipRef.current) beforeClipRef.current.style.clipPath = `inset(0 ${100 - pct}% 0 0)`;
    if (beforeLabelRef.current) beforeLabelRef.current.style.clipPath = `inset(0 ${100 - pct}% 0 0)`;
    if (afterLabelRef.current) afterLabelRef.current.style.clipPath = `inset(0 0 0 ${pct}%)`;
  }, []);

  const handleMove = useCallback((clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    if (rafId.current) cancelAnimationFrame(rafId.current);
    rafId.current = requestAnimationFrame(() => updateSlider(pct));
  }, [updateSlider]);

  useEffect(() => {
    const onMouseMove = (e) => { if (isDragging.current) handleMove(e.clientX); };
    const onMouseUp = () => { isDragging.current = false; };
    const onTouchMove = (e) => { if (isDragging.current) handleMove(e.touches[0].clientX); };
    
    window.addEventListener('mousemove', onMouseMove, { passive: true });
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onMouseUp);
    
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onMouseUp);
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, [handleMove]);

  const handleDown = (e) => {
    isDragging.current = true;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    handleMove(clientX);
  };

  const handleImageLoad = (e, setLoaded) => {
    if (e.target.naturalWidth > 50 && e.target.naturalHeight > 50) setLoaded(true);
  };

  const imagesReady = afterLoaded && beforeLoaded;

  return (
    <div 
      ref={containerRef}
      className={`relative overflow-hidden cursor-ew-resize select-none border border-white/10 hover:border-cyan-500/50 bg-neutral-800 ${className}`}
      onMouseDown={handleDown}
      onTouchStart={handleDown}
      data-testid="before-after-slider"
    >
      {!imagesReady && (
        <div className="absolute inset-0 flex items-center justify-center z-20 bg-neutral-800">
          <div className="text-center">
            <div className="text-cyan-500/50 text-5xl mb-3">+</div>
            <span className="text-gray-500 text-sm">Add images to gallery folder</span>
          </div>
        </div>
      )}

      {/* After Image */}
      <img 
        ref={afterRef}
        src={afterImage} 
        alt={afterLabel}
        className={`block w-full h-full object-cover ${imagesReady ? 'opacity-100' : 'opacity-0'}`}
        draggable={false}
        onLoad={(e) => handleImageLoad(e, setAfterLoaded)}
      />
      
      {/* Before Image */}
      <img 
        ref={beforeClipRef}
        src={beforeImage} 
        alt={beforeLabel}
        className={`absolute inset-0 w-full h-full object-cover ${imagesReady ? 'opacity-100' : 'opacity-0'}`}
        style={{ clipPath: 'inset(0 50% 0 0)', willChange: 'clip-path' }}
        draggable={false}
      />

      {/* Hidden loader for before image */}
      <img
        ref={beforeRef}
        src={beforeImage}
        alt=""
        className="hidden"
        onLoad={(e) => handleImageLoad(e, setBeforeLoaded)}
      />

      {/* Slider Handle */}
      {imagesReady && (
        <div 
          ref={sliderLineRef}
          className="absolute top-0 h-full w-0.5 bg-cyan-500 z-10 pointer-events-none"
          style={{ left: '50%', transform: 'translateX(-50%)', willChange: 'left' }}
        >
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-cyan-500 flex items-center justify-center shadow-lg">
            <div className="flex gap-0.5">
              <div className="w-0 h-0 border-t-[6px] border-b-[6px] border-r-[6px] border-transparent border-r-black" />
              <div className="w-0 h-0 border-t-[6px] border-b-[6px] border-l-[6px] border-transparent border-l-black" />
            </div>
          </div>
        </div>
      )}

      {/* Before Label */}
      {imagesReady && (
        <div 
          ref={beforeLabelRef}
          className="absolute inset-0 pointer-events-none"
          style={{ clipPath: 'inset(0 50% 0 0)', willChange: 'clip-path' }}
        >
          <div className="absolute bottom-4 left-4 px-3 py-1 bg-black/70 text-white text-sm font-accent tracking-wider">
            {beforeLabel}
          </div>
        </div>
      )}

      {/* After Label */}
      {imagesReady && (
        <div 
          ref={afterLabelRef}
          className="absolute inset-0 pointer-events-none"
          style={{ clipPath: 'inset(0 0 0 50%)', willChange: 'clip-path' }}
        >
          <div className="absolute bottom-4 right-4 px-3 py-1 bg-cyan-500 text-black text-sm font-accent tracking-wider font-semibold">
            {afterLabel}
          </div>
        </div>
      )}
    </div>
  );
};
